import { NoAccounts } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React from "react";
import { List } from "../@types/list";
import { IAddOrInviteResellerClientParams } from "../clients/answeringClient";
import { Layout } from "../SharedComponents/Layout";
import { logout } from "../SharedComponents/LoginGuard/loginGuard";
import { Pagination } from "../SharedComponents/Pagination";
import { SearchInput } from "../SharedComponents/SearchInput";
import FilterMenu from "../SharedComponents/FilterManager/FilterMenu";
import FilterChips from "../SharedComponents/FilterManager/FilterChips";
import { FilterOption, ActiveFilter } from "../SharedComponents/FilterManager/types";
import { calculateTotalPages } from "../utils/pagination";
import { trackGtagEvent } from "../utils/analytics";
import { AddClientModal } from "./components/modals/AddClientModal";
import { AssignLicenseModal } from "./components/modals/AssignLicenseModal";
import { PromoteClientModal } from "./components/modals/PromoteClientModal";
import ResellerClientRow from "./components/ResellerClientRow/ResellerClientRow";
import {
  IResellerClient,
  ResellerManagementController,
  ResellerClientLinkResponse,
} from "./controllers/ResellerManagementController";

interface State {
  isLoading: boolean;
  clientsList: List<IResellerClient>;
  filters: { filter: string; page: number; limit: number };
  isActionLoading: boolean;
  isResendingInvitation: boolean;
  resendingInvitationClientName: string | null;
  isAddClientModalOpen: boolean;
  isAssignLicenseModalOpen: boolean;
  preselectedClientForLicense: IResellerClient | null;
  isPromoteModalOpen: boolean;
  clientToPromote: IResellerClient | null;
  snackbar: { open: boolean; message: string; severity: "success" | "error" };
  extraFilters: ActiveFilter[];
}

class ResellerClientsManagementPage extends React.Component<{}, State> {
  private controller = new ResellerManagementController();

  constructor(props: {}) {
    super(props);
    this.state = {
      isLoading: true,
      clientsList: { data: [], total: 0, page: 1, limit: 10 },
      filters: { filter: "", page: 1, limit: 10 },
      isActionLoading: false,
      isResendingInvitation: false,
      resendingInvitationClientName: null,
      isAddClientModalOpen: false,
      isAssignLicenseModalOpen: false,
      preselectedClientForLicense: null,
      isPromoteModalOpen: false,
      clientToPromote: null,
      snackbar: { open: false, message: "", severity: "success" },
      extraFilters: [],
    };
  }

  applicationShouldLogout(): void {
    logout();
  }

  componentDidMount() {
    this.loadClients();
  }

  componentDidUpdate(prevProps: {}, prevState: State) {
    // extraFilters update
    if (prevState.extraFilters !== this.state.extraFilters) {
      this.loadClients();
    }
  }

  loadClients = async () => {
    this.setState({ isLoading: true });
    const token = localStorage.getItem("AccessToken")!;
    try {
      const combinedFilters = {
        ...this.state.filters, // filtros base
        origin: this.state.extraFilters.find(f => f.key === 'origem')?.value,
        invitationStatus: this.state.extraFilters.find(f => f.key === 'status')?.value,
      };
      const list = await this.controller.getResellerClients(
        token,
        combinedFilters
      );
      this.setState({ clientsList: list, isLoading: false });
    } catch (err: any) {
      this.genericError(
        err?.response?.data?.message || "Erro ao buscar clientes."
      );
    }
  };

  genericError = (message: string) => {
    this.setState({
      isLoading: false,
      isActionLoading: false,
      snackbar: { open: true, message, severity: "error" },
    });
  };

  handleSearch = (filter: string) => {
    this.setState(
      (prev) => ({ filters: { ...prev.filters, filter, page: 1 } }),
      this.loadClients
    );
  };
  handleFilterChange = (filter: string) => {
    this.setState((prev) => ({ filters: { ...prev.filters, filter } }));
  };
  handlePageChange = (newPage: number) => {
    this.setState(
      (prev) => {
        const totalPages =
          calculateTotalPages(prev.clientsList.total, prev.filters.limit) || 1;
        const page = Math.min(Math.max(newPage, 1), totalPages);
        if (page === prev.filters.page) {
          return null;
        }
        return { filters: { ...prev.filters, page } };
      },
      this.loadClients
    );
  };
  handleSnackbarClose = () => {
    this.setState((prev) => ({ snackbar: { ...prev.snackbar, open: false } }));
  };

  handleOpenAddClientModal = () =>
    this.setState({ isAddClientModalOpen: true });
  handleCloseAddClientModal = () =>
    this.setState({ isAddClientModalOpen: false });
  handleConfirmAddClient = async (data: IAddOrInviteResellerClientParams) => {
    this.setState({ isActionLoading: true });
    const token = localStorage.getItem("AccessToken")!;
    try {
      const response = await this.controller.addOrInviteResellerClient(token, data);
      this.trackResellerClientAdded(response);
      this.setState(
        {
          isAddClientModalOpen: false,
          isActionLoading: false,
          snackbar: {
            open: true,
            message: "Operação realizada com sucesso!",
            severity: "success",
          },
        },
        this.loadClients
      );
    } catch (err: any) {
      this.genericError(
        err?.response?.data?.message || "Erro ao adicionar cliente."
      );
    }
  };
  private trackResellerClientAdded(response: ResellerClientLinkResponse) {
    if (
      typeof window === "undefined" ||
      typeof document === "undefined" ||
      !document.cookie.includes("catvanteAnalyticsCookiesAccepted=true")
    ) {
      return;
    }

    const invitationType =
      response.origin ??
      response.invitationType ??
      (response.clientName ? "EXISTING_USER_LINKED" : "INVITED_NEW_USER");

    const payload: Record<string, unknown> = {
      invitation_type: invitationType,
    };

    if (invitationType === "EXISTING_USER_LINKED" && response.clientUid) {
      payload.client_user_id = response.clientUid;
    }

    const fbq = window.fbq;
    if (fbq) {
      fbq("trackCustom", "ResellerClientAdded", payload);
    }

    trackGtagEvent("reseller_client_added", payload);
  }

  handleOpenAssignLicenseModal = (client: IResellerClient | null = null) =>
    this.setState({
      isAssignLicenseModalOpen: true,
      preselectedClientForLicense: client,
    });
  handleCloseAssignLicenseModal = () =>
    this.setState({
      isAssignLicenseModalOpen: false,
      preselectedClientForLicense: null,
    });
  handleConfirmAssignLicense = async (data: {
    clientId: number;
    clientUid: string;
    items: { instrumentId: number; quantity: number }[];
  }) => {
    this.setState({ isActionLoading: true });
    const token = localStorage.getItem("AccessToken")!;
    try {
      await this.controller.assignResellerLicenses(
        token,
        data.clientId,
        data.items
      );
      this.trackResellerInstanceAssigned(data.clientUid, data.items);
      this.setState({
        isAssignLicenseModalOpen: false,
        isActionLoading: false,
        snackbar: {
          open: true,
          message: "Licenças distribuídas com sucesso!",
          severity: "success",
        },
      });
    } catch (err: any) {
      this.genericError(
        err?.response?.data?.message || "Erro ao distribuir licenças."
      );
    }
  };

  private trackResellerInstanceAssigned(
    clientUid: string,
    items: { instrumentId: number; quantity: number }[]
  ) {
    if (!clientUid) {
      return;
    }
    if (
      typeof window === "undefined" ||
      typeof document === "undefined" ||
      !document.cookie.includes("catvanteAnalyticsCookiesAccepted=true")
    ) {
      return;
    }

    const fbq = window.fbq;
    const gtagPayloadBase = {
      assigned_user_id: clientUid,
    };
    items.forEach((item) => {
      const eventPayload = {
        test_id: item.instrumentId,
        quantity: item.quantity,
        assigned_user_id: clientUid,
      };
      if (fbq) {
        fbq("trackCustom", "ResellerInstanceAssigned", eventPayload);
      }
      trackGtagEvent("reseller_instance_assigned", {
        ...gtagPayloadBase,
        test_id: item.instrumentId,
        quantity: item.quantity,
      });
    });
  }

  handleOpenPromoteModal = (client: IResellerClient) =>
    this.setState({ isPromoteModalOpen: true, clientToPromote: client });
  onClosePromotion = () =>
    this.setState({ isPromoteModalOpen: false, clientToPromote: null });
  onConfirmPromotion = async (cpf: string, crp: string) => {
    this.setState({ isActionLoading: true });
    const token = localStorage.getItem("AccessToken")!;
    try {
      await this.controller.requestPsychologistPromotion(token, {
        clientUid: this.state.clientToPromote!.clientUid,
        cpf,
        crp,
      });
      this.setState(
        {
          isPromoteModalOpen: false,
          isActionLoading: false,
          snackbar: {
            open: true,
            message: "Cliente promovido a psicólogo com sucesso.",
            severity: "success",
          },
        },
        this.loadClients
      );
    } catch (err: any) {
      this.genericError(
        err?.response?.data?.message || "Erro ao promover cliente."
      );
    }
  };

  handleResendInvitation = async (client: IResellerClient) => {
    this.setState({
      isResendingInvitation: true,
      resendingInvitationClientName: client.clientName,
    });
    const token = localStorage.getItem("AccessToken")!;
    try {
      await this.controller.resendResellerInvitation(token, client.clientUid);
      this.setState(
        {
          isResendingInvitation: false,
          resendingInvitationClientName: null,
          snackbar: {
            open: true,
            message: "Convite reenviado com sucesso!",
            severity: "success",
          },
        },
        this.loadClients
      );
    } catch (err: any) {
      this.setState({
        isResendingInvitation: false,
        resendingInvitationClientName: null,
        snackbar: {
          open: true,
          message: err?.response?.data?.message || "Erro ao reenviar convite.",
          severity: "error",
        },
      });
    }
  };

  private availableFilters: FilterOption[] = [
    {
      key: "status",
      label: "Status",
      values: [
        { label: "Aceito", value: "ACCEPTED" },
        { label: "Pendente", value: "PENDING" },
      ],
    },
    {
      key: "origem",
      label: "Origem",
      values: [
        { label: "Já tinha conta", value: "EXISTING_USER_LINKED" },
        { label: "Convidado", value: "INVITED_NEW_USER" },
      ],
    },
  ];

  render() {
    const {
      isLoading,
      clientsList,
      filters,
      snackbar,
      isAddClientModalOpen,
      isAssignLicenseModalOpen,
      preselectedClientForLicense,
      isPromoteModalOpen,
      clientToPromote,
      isActionLoading,
      isResendingInvitation,
      resendingInvitationClientName,
    } = this.state;
    const totalPages = calculateTotalPages(clientsList.total, filters.limit);

    return (
      <Layout>
        <Box
          flex={1}
          overflow="auto"
          display="flex"
          flexDirection="column"
          alignItems="center"
        >
          <Container
            maxWidth="lg"
            style={{
              padding: "80px 20px",
            }}
          >
            <Box
              marginBottom={2}
              sx={{
                display: 'flex',
                flexDirection: {
                  xs: 'column',
                  sm: 'row'
                },
                gap: '0.75rem',
                alignItems: {
                  sm: 'center'
                },
                justifyContent: {
                  xs: 'space-between',
                },
                flexWrap: 'wrap'
              }}
            >
              <Stack direction="column" spacing={1.25}>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <Typography variant="h2">Gestão de Clientes</Typography>
                  <Chip
                    label={`${this.state.clientsList?.total ?? 0} ${
                      this.state.clientsList?.total !== 1
                        ? "encontrados"
                        : "encontrado"
                    }`}
                    size="small"
                  />
                </Box>

                <Typography variant="subtitle1" color="text.secondary">
                  Acompanhe e potencialize sua base de clientes
                </Typography>
              </Stack>


              <Box display={"flex"} gap={1} flexWrap="wrap">
                <Box
                  component={Button}
                  alignSelf="end"
                  variant="contained"
                  flexWrap="wrap"
                  onClick={() => this.handleOpenAssignLicenseModal(null)}
                >
                  Distribuir Licenças
                </Box>
                <Box
                  component={Button}
                  alignSelf="end"
                  variant="contained"
                  sx={{
                    display: "flex",
                    height: 40,
                    borderRadius: 40 / 2,
                  }}
                  flexWrap="wrap"
                  onClick={this.handleOpenAddClientModal}
                >
                  Adicionar Cliente
                </Box>
              </Box>

            </Box>

            <Box marginBottom={2}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: {
                    xs: 'column',
                    sm: 'row'
                  },
                  gap: {
                    xs: '1rem',
                  },
                  alignItems: {
                    sm: 'center'
                  },
                  justifyContent: {
                    xs: 'end',

                  },
                  flexWrap: 'wrap'
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: {
                      xs: 'column',
                      sm: 'row'
                    },
                    gap: '0.75rem',
                    alignItems: {
                      sm: 'center'
                    },
                    justifyContent: {
                      xs: 'start',
                      md: 'end'
                    },
                    flexWrap: 'wrap'
                  }}
                >
                  <SearchInput
                    label="Pesquisar por nome ou email..."
                    onChangeFilter={this.handleFilterChange}
                    onSearch={this.handleSearch}
                  />

                  <FilterMenu
                    availableFilters={this.availableFilters}
                    extraFilters={this.state.extraFilters}
                    onChange={(filters) => this.setState({ extraFilters: filters })}
                  />
                </Box>
              </Box>

              <FilterChips
                availableFilters={this.availableFilters}
                extraFilters={this.state.extraFilters}
                onChange={(filters) => this.setState({ extraFilters: filters })}
              />
            </Box>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell style={{ width: "5%" }} />
                    <TableCell>Nome do Cliente</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Cliente Desde</TableCell>
                    <TableCell>Origem</TableCell>
                    <TableCell>Status do convite</TableCell>
                    <TableCell align="right">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading && clientsList.data.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : clientsList.data.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                          py={4}
                        >
                          <NoAccounts
                            color="disabled"
                            sx={{ fontSize: 48, mb: 1 }}
                          />
                          <Typography variant="subtitle1" color="textSecondary">
                            Nenhum cliente encontrado.
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Tente ajustar o filtro de busca ou adicione um novo
                            cliente.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    clientsList.data.map((client) => (
                      <ResellerClientRow
                        key={client.clientId}
                        client={client}
                        onPromoteClick={this.handleOpenPromoteModal}
                        onAssignLicenseClick={this.handleOpenAssignLicenseModal}
                        onResendInvitationClick={this.handleResendInvitation}
                        isResendingInvitation={isResendingInvitation}
                      />
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Pagination
              currentPage={filters.page}
              totalPages={totalPages}
              onChangePage={this.handlePageChange}
            />
          </Container>
        </Box>

        <AddClientModal
          isOpen={isAddClientModalOpen}
          isLoading={isActionLoading}
          onClose={this.handleCloseAddClientModal}
          onConfirm={this.handleConfirmAddClient}
        />
        <AssignLicenseModal
          isOpen={isAssignLicenseModalOpen}
          isLoading={isActionLoading}
          preselectedClient={preselectedClientForLicense}
          onClose={this.handleCloseAssignLicenseModal}
          onConfirm={this.handleConfirmAssignLicense}
        />
        {clientToPromote && (
          <PromoteClientModal
            isOpen={isPromoteModalOpen}
            isLoading={isActionLoading}
            clientName={clientToPromote.clientName}
            delegate={this}
          />
        )}
        <Dialog
          open={isResendingInvitation}
          fullWidth
          maxWidth="xs"
          disableEscapeKeyDown
          variant="modal"
        >
          <DialogContent
            variant="modal"
            sx={{ display: "flex", alignItems: "center", gap: 2 }}
          >
            <CircularProgress size={24} />
            <Box>
              <Typography variant="subtitle1">Reenviando convite...</Typography>
              <Typography variant="body2" color="text.secondary">
                {resendingInvitationClientName || "Aguarde"}
              </Typography>
            </Box>
          </DialogContent>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={this.handleSnackbarClose}
        >
          <Alert
            onClose={this.handleSnackbarClose}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Layout>
    );
  }
}

export default ResellerClientsManagementPage;
